import { API } from "apis";
import FileInput from "components/common/input/FileInput";
import { PaymentTerms, PaymentTermsInfo } from "enums";
import { CandidateItemSetModel, ContextModel } from "models";
import { CommonFileModel } from "models/CommonFileModel";
import React, { ChangeEvent } from "react";
import { Utils } from "utils";
import DialogBase from "./DialogBase";
import DateInputForm from "components/corp/info/forms/DateInputForm";
import NoteTransactionsDialog from "./NoteTransactionsDialog";

type PROPS = {
  ctx: ContextModel;
  modalIsOpen: boolean;
  targets: CandidateItemSetModel[];
  close: (result: boolean) => void;
};

/** 「見積依頼内容登録」ダイアログ */
const QuoteRequestDialog: React.FC<PROPS> = (props) => {
  const currentDate = new Date();
  // Add 7 days to the current date
  const futureDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);

  /** 件名 */
  const [title, setTitle] = React.useState("");
  /** 支払条件 */
  const [paymentTerms, setPaymentTerms] = React.useState<PaymentTerms>(
    PaymentTerms._1_YOKU_GETSU
  );
  /** メッセージ */
  const [message, setMessage] = React.useState("");
  /** 回答期限  */
  const [timeLimit, setTimeLimit] = React.useState(Utils.Date.format4(futureDate));
  /** ファイル */
  const [files, setFiles] = React.useState<CommonFileModel[]>([
    { file_name: "", file_data: "", image_url: "" },
  ]);

  const [isShowModalNote, setIsShowModalNote] = React.useState(false);
  const [isValid, setIsValid] = React.useState({
    term_check: false,
    term_confirm: false,
  });

  const actionModalNote = () => {
    setIsValid((prev) => ({ ...prev, term_check: true }))
    setIsShowModalNote(!isShowModalNote);
  };

  /** 個別見積りの標品を含むかどうか */
  const hasDisCretionaryPrice = (itemSet: CandidateItemSetModel) => {
    for (const item of itemSet.candidate_item_list) {
      if (item.is_discretionary_price) {
        return true;
      }
    }
    return false;
  };

  const calcTotal = (target: CandidateItemSetModel) => {
    let sum = 0;
    for (const item of target.candidate_item_list) {
      sum += item.quantity * item.sale_price;
    }
    return Math.floor(sum);
  };

  // Check for a valid date
  function isValidDate(dateString : any) {
    if (typeof dateString !== 'string' || !/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
      return false;
    }
    return !isNaN(Date.parse(dateString));
  }

  const close = (result: boolean) => {
    setTitle("");
    setPaymentTerms(props.ctx.auth.data?.nyu_month ?? PaymentTerms._1_YOKU_GETSU);
    setMessage("");
    setFiles([{ file_name: "", file_data: "", image_url: "" }]);
    setTimeLimit(Utils.Date.format4(futureDate));

    isValid.term_check = false;
    isValid.term_confirm = false;
    setIsValid(isValid);
    props.close(result);
  };

  const onChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };
  const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const onSend = async () => {
    console.log("送信する");

    const tmp = files.filter((v) => Utils.hasFile(v));

    let s = "";
    s += `件名　　　: ${title}\n`;
    s += `支払条件　: ${paymentTerms}\n`;
    s += `メッセージ: ${message}\n`;
    for (let i = 0; i < tmp.length; i++) {
      const file = files[i];
      s += `- ${file.file_name}\n`;
    }
    console.log(s);

    props.ctx.loading.setState(true);
    try {
      const response = await API.Thread.estimateCreate({
        estimate_list: props.targets.map((v, i) => {
          return {
            receive_corp_id: v.corp_id,
            item_list: v.candidate_item_list.map((vv, ii) => {
              return {
                item_cd: vv.candidate_item_cd,
                quantity: vv.quantity,
              };
            }),
          };
        }),
        thread: {
          thread_title: title,
          payment_type: PaymentTermsInfo[paymentTerms],
          message: message,
          file: tmp,
          time_limit: timeLimit
        },
      });
      console.log(response);
      props.ctx.msgDialog.setState({
        message: "見積依頼を送信しています。<br/>詳細は見積依頼・お問い合わせ一覧画面にてご確認下さい。",
        callback: () => {
          close(true);
        },
      });
    } catch (err) {
      Utils.errorAction(props.ctx, err);
    }
    props.ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (props.modalIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "inherit";
    }
  }, [props.modalIsOpen]);

  React.useEffect(() => {
    return (() => {
      document.body.style.overflow = "inherit";
    })
  }, []);

  React.useEffect(() => {
    if(props.ctx.auth.data?.nyu_month) {
      setPaymentTerms(props.ctx.auth.data.nyu_month);
    }
  },[props.ctx.auth.data?.nyu_month])

  // Use useEffect to set the time limit when the component mounts
  React.useEffect(() => {
    // Create a default date 7 days from the current date
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    // Format the default date to the desired format (format4)
    const defaultFormattedDate = Utils.Date.format4(defaultDate);
    // Set the time limit state to the formatted default date
    setTimeLimit(defaultFormattedDate);
  }, []);

  return (
    <React.Fragment>
    {isShowModalNote && <NoteTransactionsDialog modalIsOpen={isShowModalNote} action={actionModalNote}/>}
    <DialogBase
      modalIsOpen={props.modalIsOpen}
      title="見積依頼内容を登録"
      close={() => close(false)}
    >
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="whitespace-nowrap">
            件名
            <span className="ml-2 text-red-500 font-bold">*</span>
          </div>
          <input className="w-full" value={title} onChange={onChangeTitle} />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: "35vh" }}>
          <div>見積依頼対象</div>
          {props.targets.map((target, index) => (
            <div key={index} className="px-4 py-2">
              <div>{target.corp_name}</div>
              <table className="table-fixed w-full">
                <thead>
                  <tr className="border-b border-b-black">
                    <th>商品名</th>
                    <th>数量</th>
                    <th>価格(税抜)</th>
                  </tr>
                </thead>
                <tbody>
                  {target.candidate_item_list.map((item, index2) => (
                    <tr key={index2} className="border-b">
                      <td className="text-center">{item.item_name}</td>
                      <td className="text-center">{Number(item.quantity).toLocaleString()}</td>
                      {item.is_discretionary_price ? (
                        <td className="text-center">-</td>
                      ) : (
                        <td className="text-center">
                          {Utils.addComma(item.sale_price)}円
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-t-black">
                    <th></th>
                    <th>合計(税抜)</th>
                    {hasDisCretionaryPrice(target) ? (
                      <th>-</th>
                    ) : (
                      <th>{Utils.addTotalComma(calcTotal(target))}円</th>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
        </div>
        <div className="flex items-start gap-4">
          <div className="whitespace-nowrap pt-2">
            回答期限
            <span className="ml-2 text-red-500 font-bold">*</span>
          </div>
          <div>
            <DateInputForm
              className="w-40"
              value={Utils.Date.format3(timeLimit)}
              onChange={(value) => {
                if (isValidDate(value)) {
                 // Check for a valid date
                  if (value === null) {
                    setTimeLimit("");
                  } else {
                    //replace sign / to -
                    const formattedDate = (value as any).replace(/\//g, '-');
                    setTimeLimit(formattedDate);
                  }
                }
              }}
            />
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="whitespace-nowrap pt-2">支払条件<span className="ml-2 text-red-500 font-bold invisible">*</span></div>
          <select
            className="w-40"
            value={paymentTerms}
            onChange={(v) => setPaymentTerms(parseInt(v.target.value))}
          >
            <option value={PaymentTerms._1_YOKU_GETSU}>翌月</option>
            <option value={PaymentTerms._2_YOKUYOKU_GETSU}>翌々月</option>
            <option value={PaymentTerms._3_3MONTHS}>３ヶ月</option>
          </select>
        </div>
        <div className="flex items-start gap-4">
          <div className="whitespace-nowrap pt-2">内容</div>
          <textarea
            className="w-full rounded-none"
            value={message}
            onChange={onChangeMessage}
          />
        </div>
        <div className="flex flex-col gap-2">
          {files.map((v, i) => {
            return (
              <FileInput
                key={i}
                data={v}
                onChange={(data) => {
                  const tmp = files.filter((v) => !!v.file_name);
                  if (tmp.length < 10) {
                    tmp.push({ file_name: "", file_data: "", image_url: "" });
                  }
                  setFiles(tmp);
                }}
                accept=".xlsx,.xls,.docx,.doc,.txt,.pdf,.jpg,.png,.gif,.zip"
                ctx={props.ctx}
                name="添付ファイル"
                maxSize={10}
              />
            );
          })}
        </div>

        <div className="my-4 flex justify-center flex-col items-center">
          <label htmlFor="term_check" className="ml-2">
            <input id="term_check" type="checkbox" disabled={!isValid.term_check} checked={isValid.term_check} />
            <span className="ml-2 font-medium" onClick={actionModalNote}>「取引上の注意事項」を確認</span>
          </label>
          <label htmlFor="term_confirm" className="ml-2">
            <input
              id="term_confirm"
              type="checkbox"
              disabled={!isValid.term_check}
              onChange={(e) => setIsValid((prev) => ({ ...prev, term_confirm: e.target.checked }))}
            />
            <span className="ml-2 font-medium">「取引上の注意事項」へ同意</span>
          </label>
        </div>

        <div className="flex justify-center">
          <button disabled={!(isValid.term_check && isValid.term_confirm)} className={`${!(isValid.term_check && isValid.term_confirm) ? 'secondary-disabled px-4 py-2 ' : 'secondary px-4 py-2 '}`} onClick={onSend}>
            送信する
          </button>
        </div>
      </div>
    </DialogBase>
    </React.Fragment>
  );
};

export default QuoteRequestDialog;
